import styled, { css } from 'styled-components/macro';
import { hideVisually as hide } from 'polished';
import { compose, withProps, branch, renderNothing } from 'recompose';

import modularScale from 'styles/helpers/modular-scale';
import { definitionLabel } from 'styles/base-classes';
import Header from './Header';
import Row from './Row';

export const Head = styled.thead`
  tr {
    vertical-align: bottom;

    &:hover {
      background: none;
    }
  }
`;

const activeButton = ({ theme }) => css`
  color: ${theme.NightBlue};

  span {
    opacity: 1;
  }
`;

const SortingArrows = styled.span`
  margin-left: ${({ theme }) => theme.spacing.xsmall};
  margin-bottom: 0.25em;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
`;

const ArrowWrapper = styled.span`
  font-size: ${modularScale(-2)};
  line-height: 0.7;
  color: ${({ theme, isActive }) =>
    isActive ? theme.DeepBlue : theme.CasperGrey};
  transform: scaleY(0.75);
`;

const hideIfNotActiveOrDescending = (notActiveOrDescending) =>
  branch(notActiveOrDescending, renderNothing);

const SortArrow = compose(
  hideIfNotActiveOrDescending((props) => {
    if (props.isActiveColumn) return false;
    if (props.direction === 'descending') return false;
    return true;
  }),
  withProps((props) => {
    return {
      isActive:
        props.currentDirection === props.direction && props.isActiveColumn,
    };
  })
)(({ children, isActive }) => (
  <ArrowWrapper isActive={isActive}>{children}</ArrowWrapper>
));

const Button = styled.button`
  ${({ isActive }) => isActive && activeButton};
  padding: 0;
  transition: ${({ theme }) =>
    `color ${theme.transitionDuration} ${theme.timingFunction}`};
  color: ${({ theme }) => theme.CasperGrey};
  cursor: default;

  &:hover {
    color: ${({ theme }) => theme.NightBlue};
  }
`;

const ButtonContent = styled.span`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-end;
  justify-content: flex-end;
  text-align: right;
`;

export const Label = styled.span`
  ${definitionLabel};
  ${({ hideVisually }) => hideVisually && hide()}
  color: inherit;
`;

const TableHead = ({
  columnHeaders,
  handleClick,
  activeHeader,
  direction,
  className,
}) => (
  <Head className={className}>
    <Row>
      {columnHeaders.map((header) => {
        const { value, label, hideVisually } = header;
        const isActive = value === activeHeader;
        const isNonInteractive = value === 'countryRank' || value === 'expand';

        return (
          <Header
            id={value}
            key={value}
            aria-sort={isActive ? direction : null}
          >
            {isNonInteractive ? (
              <ButtonContent>
                <Label hideVisually={hideVisually}>{label}</Label>
              </ButtonContent>
            ) : (
              <Button
                data-key={value}
                onClick={() => handleClick(header)}
                isActive={isActive}
              >
                <ButtonContent>
                  <Label>{label}</Label>
                  <SortingArrows>
                    <SortArrow
                      direction="ascending"
                      currentDirection={direction}
                      isActiveColumn={isActive}
                    >
                      &#9650;
                    </SortArrow>
                    <SortArrow
                      direction="descending"
                      currentDirection={direction}
                      isActiveColumn={isActive}
                    >
                      &#9660;
                    </SortArrow>
                  </SortingArrows>
                </ButtonContent>
              </Button>
            )}
          </Header>
        );
      })}
    </Row>
  </Head>
);

const enhance = compose(
  withProps(({ order }) => ({
    direction: order === 'asc' ? 'ascending' : 'descending',
  }))
);

export default enhance(TableHead);
